import Style from './userlist.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { IStaffDto, IStaffStudyDto, ITagFiltersDto } from '../../../services/api/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useCommonLocalization } from '../../../application/localisation/useCommonLocalization';
import { useForm } from 'react-hook-form';
import { useModal } from '../../../application/hooks/useModal';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { useGetStudyRolesQuery } from 'src/services/api/api-client/UserRolesQuery';
import { HookFormCheckboxGroupField } from 'src/hookFormControls/HookFormCheckboxGroupField';
import { Deferred } from 'src/helpers/Deferred';
import { handleSubmitFormError } from 'src/application/error-handling/useErrorHandler';
import { HookFormTextInput } from 'src/hookFormControls/hookFormTextInput';
import { getAllUsersQueryKey } from 'src/services/api/api-client/UsersQuery';
import { changeUserStudyRoles, changeUserTagFilters } from 'src/services/api/api-client/UsersClient';
import { AppInputError, AppInputLabel } from 'uikit/wrappers';
import { BulletPoints } from 'uikit/BulletPoints';
import { useStaffProfile } from 'src/helpers/hooks/useStudy';
import { conditionalConcat } from 'src/helpers/arrayHelpers';
import { getDoctorProfileInfoQueryKey } from 'src/services/api/api-client/ProfileQuery';
import { getDoctorStudiesQueryKey, useGetStudyAllTagsQuery } from 'src/services/api/api-client/StudyQuery';
import { Loading } from 'uikit/suspense/Loading';
import { HookFormTagEditor, TagEditorValueItem } from 'src/hookFormControls/HookFormTagEditor';

type FormFieldType = {
  staffId: string;
  studyId: number;
  studyNumber: string;
  email: string | null;
  studyRoles: string[];
  studyTags: TagEditorValueItem[];
};

const mapTagFiltersToArray = (tagFilters: ITagFiltersDto | null | undefined): TagEditorValueItem[] =>
  Object.entries(tagFilters ?? {}).map(([key, values]) => ({
    key,
    values: Array.isArray(values) ? values : [],
  }));

const mapTagArrayToFilters = (tags: TagEditorValueItem[]): ITagFiltersDto =>
  tags.reduce<ITagFiltersDto>((acc, item) => {
    const normalizedKey = item.key?.trim();
    if (!normalizedKey) return acc;
    acc[normalizedKey] = Array.from(new Set([...(acc[normalizedKey] ?? []), ...(item.values ?? [])]));
    return acc;
  }, {});

export const useEditUserStudyModal = () => {
  const client = useQueryClient();
  const { t } = useTranslation('dev');
  const commonLocalizer = useCommonLocalization();
  const { openModal, closeModal, visible } = useModal();
  const deferred = useRef<Deferred<void>>();
  const [staffStudy, setStaffStudy] = useState<IStaffStudyDto>();
  const { profile } = useStaffProfile();

  const form = useForm<FormFieldType>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  const {
    clearErrors,
    setError,
    trigger,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
    watch,
    control,
    setValue,
  } = form;

  const studyNumber = watch('studyNumber');
  const studyId = watch('studyId');
  const newRoles = watch('studyRoles');
  const studyTags = watch('studyTags');
  const prevStudyTagsRef = useRef<FormFieldType['studyTags']>();

  const { data: roleList, refetch, isInitialLoading } = useGetStudyRolesQuery(studyId, true, { enabled: !!studyId });

  const { data: allTags } = useGetStudyAllTagsQuery(studyId, { enabled: !!studyId });

  const availableForms = useMemo(
    () =>
      [
        ...new Set(
          roleList?.filter((x) => newRoles?.includes(x.id)).flatMap((x) => x.availableForms.map((f) => f.type)),
        ),
      ].sort(),
    [newRoles, roleList],
  );

  const onSubmit = handleSubmit(async (data: FormFieldType) => {
    try {
      const tagFilters = mapTagArrayToFilters(data.studyTags ?? []);
      await Promise.all([
        changeUserStudyRoles(data.staffId, data.studyRoles ?? [], data.studyId),
        changeUserTagFilters(data.staffId, data.studyId, tagFilters),
      ]);
      await Promise.all(
        conditionalConcat(
          [client.invalidateQueries(getAllUsersQueryKey())],
          data.staffId === profile.id && [
            client.invalidateQueries(getDoctorProfileInfoQueryKey()),
            client.invalidateQueries(getDoctorStudiesQueryKey()),
          ],
        ),
      );

      closeModal();
    } catch (e: any) {
      handleSubmitFormError(e, setError);
    }
  });

  const handleSaveClick = async () => {
    const isValid = await trigger(undefined, { shouldFocus: true });
    if (!isValid) return;

    await onSubmit();
  };

  const roleOptions = useMemo(() => roleList?.map((x) => ({ key: x.id, text: x.name })) || [], [roleList]);

  useEffect(() => {
    const sr = roleOptions.filter((x) => staffStudy?.roles.map((r) => r.id).includes(x.key)).map((x) => x.key);
    setValue('studyRoles', sr);
  }, [roleOptions, setValue, staffStudy?.roles]);

  // Clear tag filter errors when tags are changed
  useEffect(() => {
    const prevStudyTags = prevStudyTagsRef.current;
    prevStudyTagsRef.current = studyTags;

    if (prevStudyTags === undefined || prevStudyTags === studyTags) return;
    if (!(errors.studyTags as FieldError | undefined)?.message) return;

    clearErrors('studyTags');
  }, [clearErrors, errors.studyTags, studyTags]);

  return {
    start: async (staff: IStaffDto, s: IStaffStudyDto) => {
      reset({
        staffId: staff.id,
        email: staff.email,
        studyId: s.id,
        studyNumber: s.studyNumber,
        studyTags: mapTagFiltersToArray(s.tagFilters),
      });

      setStaffStudy(s);

      openModal();
      await refetch();

      deferred.current = new Deferred();
      return deferred.current.promise;
    },
    modal: (
      <DialogModal
        visible={visible}
        onHide={() => {
          closeModal();
          deferred.current?.resolve();
        }}
        title={t('Admin.UserList.Details.EditUserStudyRolesModal.Title')}
        subTitle={studyNumber}
        bodyClassName={Style.modalBody}
        footer={{
          errors: errors?.root?.message,
          leftButton: {
            onClick: () => {
              closeModal();
              deferred.current?.resolve();
            },
            text: commonLocalizer('Common_Cancel'),
            disabled: isSubmitting,
          },
          rightButton: {
            onClick: handleSaveClick,
            text: commonLocalizer('Common_Save'),
            isLoading: isSubmitting,
            disabled: isSubmitting || !isDirty,
          },
        }}
      >
        <Loading loading={isInitialLoading} testId={'edit-user-study'}>
          <HookFormTextInput
            disabled={true}
            control={control}
            name={'email'}
            labelProps={{ text: t('Admin.UserList.Details.EditUserStudyRolesModal.EmailLabel') }}
          />

          <HookFormCheckboxGroupField
            control={control}
            name={'studyRoles'}
            withSelectAllButton={true}
            labelProps={{ text: t('Admin.UserList.Details.EditUserStudyRolesModal.StudyRoles'), isBold: true }}
            options={roleOptions}
            valueField={'key'}
            labelField={'text'}
          />

          <AppInputError>
            <AppInputLabel isBold text={t('Admin.UserList.Details.AvailableRecords')}>
              {availableForms.length > 0 ? (
                <BulletPoints values={availableForms} />
              ) : (
                t('Admin.UserList.Details.NoAvailableRecords')
              )}
            </AppInputLabel>
          </AppInputError>
          <HookFormTagEditor
            control={form.control}
            name={'studyTags'}
            suggestions={allTags ?? {}}
            canRemoveAllTags={true}
            allowFreeInputTags={true}
          />
        </Loading>
      </DialogModal>
    ),
  };
};
